import { createContext, memo, type ReactNode, useContext, useMemo } from 'react';

import { reportErrorToBugsnag } from '@air/utils-error';

import { isDevOrTestStage } from '~/swr-hooks/utils';

export type WorkspaceAgentComposerWorkspaceContextValue = {
  workspaceId: string | undefined;
};

const defaultWorkspaceAgentComposerWorkspaceContextValue: WorkspaceAgentComposerWorkspaceContextValue = {
  workspaceId: undefined,
};

const WorkspaceAgentComposerWorkspaceContext = createContext<WorkspaceAgentComposerWorkspaceContextValue>(
  defaultWorkspaceAgentComposerWorkspaceContextValue,
);

export type WorkspaceAgentComposerWorkspaceProviderProps = {
  children: ReactNode;
  workspaceId: string | undefined;
};

/**
 * Supplies `workspaceId` for composer/moniker surfaces outside `PrivateWorkspaceAgentPanelProvider`
 * (e.g. skills instructions). Panel pill chips and mention search read this when set.
 */
export const WorkspaceAgentComposerWorkspaceProvider = memo(
  ({ children, workspaceId }: WorkspaceAgentComposerWorkspaceProviderProps) => {
    const value = useMemo(() => ({ workspaceId }), [workspaceId]);

    return (
      <WorkspaceAgentComposerWorkspaceContext.Provider value={value}>
        {children}
      </WorkspaceAgentComposerWorkspaceContext.Provider>
    );
  },
);

WorkspaceAgentComposerWorkspaceProvider.displayName = 'WorkspaceAgentComposerWorkspaceProvider';

export const useWorkspaceAgentComposerWorkspaceId = (): string | undefined => {
  const context = useContext(WorkspaceAgentComposerWorkspaceContext);

  if (context === defaultWorkspaceAgentComposerWorkspaceContextValue) {
    const error = new Error(
      'useWorkspaceAgentComposerWorkspaceId must be used within WorkspaceAgentComposerWorkspaceProvider',
    );

    if (isDevOrTestStage()) {
      throw error;
    }

    reportErrorToBugsnag({ error, context: 'Workspace agent composer workspace context not found' });
  }

  return context.workspaceId;
};
